// src/views/admin/CalendarPage.js
// Enhanced Calendar - Production Ready with Advanced Features & Premium UI

import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';

import {
    Box, Flex, Grid, SimpleGrid, Text, Heading, Button, IconButton,
    useColorModeValue, useColorMode, VStack, Tag, HStack, useDisclosure, Modal, ModalOverlay,
    ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Input, Textarea, useToast, Alert, AlertIcon,
    AlertTitle, AlertDescription, Divider, Icon, Select, Menu, MenuButton,
    MenuList, MenuItem, Badge, Tooltip, Stat, StatLabel, StatNumber,
    Switch, FormHelperText, Skeleton, Progress, Tabs, TabList, TabPanels,
    Tab, TabPanel, NumberInput, NumberInputField, NumberInputStepper,
    NumberIncrementStepper, NumberDecrementStepper, Checkbox, CheckboxGroup,
    Stack, Radio, RadioGroup, Wrap, WrapItem, AvatarGroup, Avatar, Circle,
    ScaleFade, Collapse, useBreakpointValue
} from '@chakra-ui/react';

import {
    MdChevronLeft, MdChevronRight, MdDelete, MdRefresh, MdFilterList,
    MdFileDownload, MdPrint, MdAdd, MdCalendarToday, MdEvent, MdDashboard,
    MdSettings, MdNotifications, MdPeople, MdPerson, MdClose, MdCheckCircle,
    MdAccessTime, MdLocationOn, MdAttachFile, MdRepeat, MdAlarm, MdLabel,
    MdInfoOutline, MdWarning, MdStar, MdEdit, MdVisibility, MdToday,
    MdKeyboardArrowDown, MdKeyboardArrowUp, MdCheck, MdOutlineCalendarMonth,
    MdSchedule, MdTune, MdBolt, MdTrendingUp, MdTimeline, MdNavigateNext,
    MdNavigateBefore, MdAutoAwesome, MdDateRange, MdMoreVert
} from 'react-icons/md';
import { VscCircleFilled } from 'react-icons/vsc';

import {
    format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
    isSameMonth, isSameDay, addMonths, subMonths, parseISO, isValid, startOfDay,
    isWeekend, addDays, isAfter, isBefore
} from 'date-fns';

import { motion, AnimatePresence } from 'framer-motion';

import { config } from '../../config';
import { getAuthToken } from '../../utils/auth';
import ViewWorkOrderModal from '../../components/work_orders/ViewWorkOrderModal';
import { fetchUsers } from '../../services/userService';
import { fetchTeams } from '../../services/teamService';
import { useDebounce, useAsync, useInterval, useLocalStorage } from '../../utils/hooks';
import ErrorBoundary from '../../components/ErrorBoundary';
import { LoadingOverlay, ConfirmDialog, EmptyState, DataStatus } from '../../components/common/LoadingComponents';

// ─────────────────────────────────────────────
// MOTION PRIMITIVES
// ─────────────────────────────────────────────
const MotionBox        = motion(Box);
const MotionFlex       = motion(Flex);
const MotionSimpleGrid = motion(SimpleGrid);
const MotionBadge      = motion(Badge);
const MotionVStack     = motion(VStack);

// ─────────────────────────────────────────────
// ANIMATION VARIANTS
// ─────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.4, staggerChildren: 0.07 } },
    exit:    { opacity: 0, transition: { duration: 0.2 } }
};

const slideUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } }
};

const scaleIn = {
    initial: { opacity: 0, scale: 0.93 },
    animate: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 320, damping: 28 } }
};

const stagger = {
    animate: { transition: { staggerChildren: 0.06, delayChildren: 0.08 } }
};

const cellAnim = {
    initial: { opacity: 0, scale: 0.88 },
    animate: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 340, damping: 30 } }
};

const chipAnim = {
    initial: { opacity: 0, x: -10 },
    animate: { opacity: 1, x: 0,  transition: { type: 'spring', stiffness: 300, damping: 26 } },
    exit:    { opacity: 0, x: 10, transition: { duration: 0.16 } }
};

const float = {
    y: [0, -8, 0],
    transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' }
};

const pulse = {
    scale: [1, 1.08, 1],
    transition: { duration: 2.2, repeat: Infinity, repeatType: 'reverse' }
};

const breathe = {
    opacity: [0.5, 1, 0.5],
    transition: { duration: 2.6, repeat: Infinity, ease: 'easeInOut' }
};

const rotate = {
    rotate: 360,
    transition: { duration: 1.2, repeat: Infinity, ease: 'linear' }
};

// ─────────────────────────────────────────────
// HOLIDAYS
// ─────────────────────────────────────────────
const HOLIDAYS_2025 = [
    { date: '2025-01-01', name: "New Year's Day",             type: 'federal' },
    { date: '2025-01-20', name: 'Martin Luther King Jr. Day', type: 'federal' },
    { date: '2025-02-17', name: "Presidents' Day",            type: 'federal' },
    { date: '2025-05-26', name: 'Memorial Day',               type: 'federal' },
    { date: '2025-06-19', name: 'Juneteenth',                 type: 'federal' },
    { date: '2025-07-04', name: 'Independence Day',           type: 'federal' },
    { date: '2025-09-01', name: 'Labor Day',                  type: 'federal' },
    { date: '2025-10-13', name: 'Columbus Day',               type: 'federal' },
    { date: '2025-11-11', name: 'Veterans Day',               type: 'federal' },
    { date: '2025-11-27', name: 'Thanksgiving Day',           type: 'federal' },
    { date: '2025-12-25', name: 'Christmas Day',              type: 'federal' },
];

// ─────────────────────────────────────────────
// API HELPERS
// ─────────────────────────────────────────────
const API_BASE_URL = config.apiUrl;

const handleApiError = async (response, resourceName = 'resource') => {
    let errorMsg = `HTTP error ${response.status} fetching ${resourceName}: ${response.statusText}`;
    if (response.headers.get("content-type")?.includes("application/json")) {
        try {
            const errData = await response.json();
            errorMsg = errData.message || (errData.errors ? errData.errors.map(e => e.msg).join(', ') : errorMsg);
        } catch (parseErr) {
            errorMsg = `Received non-JSON error response (Status: ${response.status} ${response.statusText})`;
        }
    } else {
        errorMsg = `Received non-JSON response (Status: ${response.status} ${response.statusText})`;
    }
    throw new Error(errorMsg);
};

const fetchUnifiedCalendarData = async (token, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.technicianId) params.append('technicianId', filters.technicianId);
    if (filters.teamId)       params.append('teamId',       filters.teamId);
    const url = `${API_BASE_URL}/calendar-events?${params.toString()}`;
    console.log(`Fetching unified calendar data from: ${url}`);
    const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    if (!response.ok) await handleApiError(response, 'unified calendar data');
    return await response.json();
};

const createCalendarEvent = async (eventData, token) => {
    console.log("API: Creating Calendar Event:", eventData);
    const response = await fetch(`${API_BASE_URL}/calendar-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(eventData)
    });
    if (!response.ok) await handleApiError(response, 'creating calendar event');
    return await response.json();
};

const deleteCalendarEvent = async (eventId, token) => {
    console.log(`API: Deleting Calendar Event: ${eventId}`);
    const response = await fetch(`${API_BASE_URL}/calendar-events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) await handleApiError(response, 'deleting calendar event');
    try { return await response.json(); }
    catch (e) {
        if (response.status === 204 || response.status === 200) return { message: 'Event deleted successfully' };
        throw new Error("Error parsing delete response.");
    }
};

// ─────────────────────────────────────────────
// PURE HELPERS
// ─────────────────────────────────────────────
const getHolidayForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return HOLIDAYS_2025.find(h => h.date === dateStr);
};

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const exportToCSV = (items) => {
    const headers = ['Date', 'Title', 'Type', 'Description'];
    const rows = items.map(item => [
        format(parseISO(item.start), 'yyyy-MM-dd HH:mm'),
        item.title,
        item.extendedProps?.type || 'event',
        item.description || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `calendar-${format(new Date(), 'yyyy-MM-dd')}.csv`; a.click();
    window.URL.revokeObjectURL(url);
};

const printCalendar = () => window.print();

const EVENT_CONFIG = {
    work_order:   { color: 'orange', label: 'Work Order'   },
    maintenance:  { color: 'green',  label: 'Maintenance'  },
    manual_event: { color: 'blue',   label: 'Manual Event' },
};
const getECfg = (type) => EVENT_CONFIG[type] || EVENT_CONFIG.manual_event;

// ─────────────────────────────────────────────
// PULSE DOT
// ─────────────────────────────────────────────
const PulseDot = memo(({ color = 'green', size = '8px' }) => (
    <Box position="relative" display="inline-flex" alignItems="center" justifyContent="center">
        <MotionBox
            position="absolute" borderRadius="full"
            w={size} h={size} bg={`${color}.400`}
            animate={{ scale: [1, 2.4, 1], opacity: [0.8, 0, 0.8] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
        />
        <Box borderRadius="full" w={size} h={size} bg={`${color}.500`} />
    </Box>
));

// ─────────────────────────────────────────────
// ANIMATED COUNTER
// ─────────────────────────────────────────────
const AnimatedCounter = memo(({ value, color }) => {
    const [display, setDisplay] = useState(0);
    const prev = useRef(0);
    useEffect(() => {
        const start = prev.current;
        const end   = Number(value) || 0;
        prev.current = end;
        if (start === end) return;
        const steps = 36;
        const inc   = (end - start) / steps;
        let step    = 0;
        const timer = setInterval(() => {
            step++;
            setDisplay(Math.round(start + inc * step));
            if (step >= steps) { setDisplay(end); clearInterval(timer); }
        }, 1000 / 60);
        return () => clearInterval(timer);
    }, [value]);
    return <Text as="span" color={color}>{display.toLocaleString()}</Text>;
});

// ─────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────
const StatsCard = memo(({ label, value, icon: IconComp, color = 'blue', isLoading, subtitle, index = 0 }) => {
    const cardBg    = useColorModeValue('white', 'gray.800');
    const border    = useColorModeValue('gray.100', 'gray.700');
    const mutedC    = useColorModeValue('gray.500', 'gray.400');
    const headingC  = useColorModeValue('gray.800', 'white');
    const iconBg    = useColorModeValue(`${color}.50`, `${color}.900`);
    const shadow    = useColorModeValue('0 4px 24px rgba(0,0,0,0.06)', '0 4px 24px rgba(0,0,0,0.3)');

    return (
        <MotionBox
            custom={index}
            variants={{
                initial: { opacity: 0, y: 20 },
                animate: { opacity: 1, y: 0, transition: { delay: index * 0.08, type: 'spring', stiffness: 280, damping: 26 } }
            }}
            initial="initial" animate="animate"
            whileHover={{ y: -5, transition: { duration: 0.18 } }}
        >
            <Box
                bg={cardBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={border}
                boxShadow={shadow} borderLeftWidth="4px" borderLeftColor={`${color}.400`}
                position="relative" overflow="hidden"
                _hover={{ boxShadow: '0 8px 40px rgba(0,0,0,0.14)' }}
                transition="box-shadow 0.2s"
            >
                <Box
                    position="absolute" top="-30px" right="-30px"
                    w="100px" h="100px" borderRadius="full"
                    bg={`${color}.100`} opacity={0.2} filter="blur(24px)" pointerEvents="none"
                />
                {isLoading ? (
                    <Skeleton h="70px" borderRadius="xl" />
                ) : (
                    <Flex align="center" justify="space-between" position="relative">
                        <Box flex="1" minW="0">
                            <Text fontSize="10px" fontWeight="700" color={mutedC} mb={2}
                                textTransform="uppercase" letterSpacing="0.08em" noOfLines={1}
                            >
                                {label}
                            </Text>
                            <Text fontSize="3xl" fontWeight="800" color={headingC} lineHeight="1" mb={1}>
                                <AnimatedCounter value={value} color={`${color}.500`} />
                            </Text>
                            {subtitle && <Text fontSize="xs" color={mutedC} mt={1} noOfLines={1}>{subtitle}</Text>}
                        </Box>
                        <MotionBox animate={value > 0 && color === 'red' ? pulse : {}}>
                            <Circle size="54px" bg={iconBg} boxShadow="0 4px 14px rgba(0,0,0,0.08)" ml={3} flexShrink={0}>
                                <Icon as={IconComp} boxSize={6} color={`${color}.500`} />
                            </Circle>
                        </MotionBox>
                    </Flex>
                )}
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────
// LEGEND
// ─────────────────────────────────────────────
const CalendarLegend = memo(() => {
    const textC   = useColorModeValue('gray.600', 'gray.300');
    const cardBg  = useColorModeValue('white', 'gray.800');
    const border  = useColorModeValue('gray.100', 'gray.700');
    return (
        <MotionBox variants={slideUp} initial="initial" animate="animate">
            <Box bg={cardBg} px={5} py={3} borderRadius="xl" borderWidth="1px"
                borderColor={border} boxShadow="sm" mb={5}
            >
                <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
                    <HStack spacing={1}>
                        <Icon as={MdOutlineCalendarMonth} boxSize={4} color="blue.500" />
                        <Text fontSize="10px" fontWeight="700" color={textC} textTransform="uppercase" letterSpacing="0.08em">Legend</Text>
                    </HStack>
                    <HStack spacing={5} flexWrap="wrap">
                        {[
                            { color: 'blue.500',   icon: VscCircleFilled, label: 'Manual Event' },
                            { color: 'orange.500', icon: VscCircleFilled, label: 'Work Order'   },
                            { color: 'green.500',  icon: VscCircleFilled, label: 'Maintenance'  },
                            { color: 'yellow.500', icon: MdStar,          label: 'Holiday'      },
                            { color: 'teal.400',   icon: MdToday,         label: 'Today'        },
                        ].map(({ color, icon, label }) => (
                            <MotionBox key={label} whileHover={{ scale: 1.06 }} transition={{ duration: 0.14 }}>
                                <HStack spacing={1.5}>
                                    <Icon as={icon} color={color} boxSize={3.5} />
                                    <Text fontSize="xs" color={textC} fontWeight="500">{label}</Text>
                                </HStack>
                            </MotionBox>
                        ))}
                    </HStack>
                </Flex>
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────
// EVENT CHIP
// ─────────────────────────────────────────────
const EventChip = memo(({ item, onItemClick, onDeleteClick, index }) => {
    const cfg      = getECfg(item.extendedProps?.type);
    const chipBg   = useColorModeValue(`${cfg.color}.50`,  `${cfg.color}.900`);
    const chipC    = useColorModeValue(`${cfg.color}.700`, `${cfg.color}.200`);
    const chipBord = useColorModeValue(`${cfg.color}.200`, `${cfg.color}.700`);
    return (
        <MotionBox variants={chipAnim} initial="initial" animate="animate" exit="exit" custom={index}>
            <Flex
                bg={chipBg} px={1.5} py={0.5} borderRadius="md"
                borderWidth="1px" borderColor={chipBord}
                align="center" justify="space-between" cursor="pointer"
                onClick={(e) => onItemClick(e, item)}
                _hover={{ opacity: 0.85, transform: 'translateX(2px)' }}
                transition="all 0.15s" gap={1} overflow="hidden"
            >
                <HStack spacing={1} flex="1" minW="0">
                    <Box w="5px" h="5px" borderRadius="full" bg={`${cfg.color}.500`} flexShrink={0} />
                    <Text fontSize="10px" fontWeight="700" color={chipC} noOfLines={1} title={item.title} flex="1">
                        {item.title}
                    </Text>
                </HStack>
                {item.extendedProps?.type === 'manual_event' && (
                    <MotionBox whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }} flexShrink={0}>
                        <IconButton
                            aria-label="Delete event"
                            icon={<Icon as={MdDelete} boxSize={2.5} />}
                            size="xs" variant="ghost" colorScheme="red"
                            minW="14px" h="14px" borderRadius="sm"
                            onClick={(e) => onDeleteClick(e, item)}
                        />
                    </MotionBox>
                )}
            </Flex>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────
// CALENDAR CELL
// ─────────────────────────────────────────────
const CalendarCell = memo(({
    day, dayItems, isCurrentMonth, isToday: isTodayCell,
    holiday, isWeekendDay,
    onDayClick, onDayDetailsClick, onItemClick, onDeleteClick,
    todayBg, holidayBg, weekendBg, cellHoverBg,
    todayBorderColor, holidayBorderColor, cellBorderColor,
    textColor, mutedColor, headingColor
}) => {
    const visibleItems = dayItems.slice(0, 2);
    const extraCount   = dayItems.length - 2;

    return (
        <MotionBox variants={cellAnim} whileHover={isCurrentMonth ? { scale: 1.02, zIndex: 2 } : {}} transition={{ duration: 0.16 }}>
            <Flex
                p={1.5}
                minH={{ base: '90px', md: '118px' }}
                direction="column"
                bg={isTodayCell ? todayBg : holiday ? holidayBg : isWeekendDay ? weekendBg : 'transparent'}
                borderWidth="1px"
                borderColor={isTodayCell ? todayBorderColor : holiday ? holidayBorderColor : cellBorderColor}
                borderRadius="xl"
                opacity={isCurrentMonth ? 1 : 0.28}
                onClick={() => isCurrentMonth && onDayClick(day)}
                onDoubleClick={(e) => isCurrentMonth && onDayDetailsClick(e, day)}
                cursor={isCurrentMonth ? 'pointer' : 'default'}
                _hover={isCurrentMonth ? { bg: cellHoverBg, boxShadow: 'sm' } : {}}
                overflow="hidden" position="relative" transition="all 0.15s"
            >
                {isTodayCell && (
                    <Box
                        position="absolute" top="0" left="0" right="0"
                        h="2.5px" bgGradient="linear(to-r, teal.400, blue.400)"
                        borderTopRadius="xl"
                    />
                )}
                <HStack justify="space-between" mb={1} mt={isTodayCell ? 0.5 : 0}>
                    <Flex
                        w={isTodayCell ? '22px' : 'auto'} h={isTodayCell ? '22px' : 'auto'}
                        borderRadius={isTodayCell ? 'full' : 'none'}
                        bg={isTodayCell ? 'teal.500' : 'transparent'}
                        align="center" justify="center" flexShrink={0}
                    >
                        <Text
                            fontSize="xs" lineHeight="1"
                            fontWeight={isTodayCell ? '800' : 'bold'}
                            color={isTodayCell ? 'white' : isWeekendDay ? mutedColor : textColor}
                        >
                            {format(day, 'd')}
                        </Text>
                    </Flex>
                    {holiday && (
                        <MotionBox animate={pulse}>
                            <Tooltip label={holiday.name} hasArrow fontSize="xs">
                                <Icon as={MdStar} boxSize={3} color="yellow.500" />
                            </Tooltip>
                        </MotionBox>
                    )}
                </HStack>
                <VStack align="stretch" spacing={0.5} flexGrow={1} overflowY="hidden">
                    <AnimatePresence>
                        {visibleItems.map((item, idx) => (
                            <EventChip
                                key={item.id} item={item} index={idx}
                                onItemClick={onItemClick} onDeleteClick={onDeleteClick}
                            />
                        ))}
                    </AnimatePresence>
                    {extraCount > 0 && (
                        <MotionBox whileHover={{ x: 2 }} transition={{ duration: 0.12 }}>
                            <Button
                                size="xs" variant="ghost" fontSize="9px" fontWeight="700"
                                color={mutedColor} h="16px" px={1.5} borderRadius="sm"
                                onClick={(e) => { e.stopPropagation(); onDayDetailsClick(e, day); }}
                                _hover={{ color: headingColor, bg: cellHoverBg }}
                            >
                                +{extraCount} more
                            </Button>
                        </MotionBox>
                    )}
                </VStack>
            </Flex>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────
// DAY DETAILS MODAL
// ─────────────────────────────────────────────
const DayDetailsModal = memo(({ isOpen, onClose, day, events, onCreateEvent, onEventClick }) => {
    // 1. Get the current color mode state safely
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const cardBg      = useColorModeValue('white', 'gray.800');
    const textC       = useColorModeValue('gray.800', 'white');
    const mutedC      = useColorModeValue('gray.600', 'gray.400');
    const border      = useColorModeValue('gray.100', 'gray.700');
    const filterBg    = useColorModeValue('gray.50', 'gray.750');
    const headingC    = useColorModeValue('gray.800', 'white');
    const iconBg      = useColorModeValue('blue.50', 'blue.900');

    // 2. Pure JS functions (No hooks inside!)
    const getBgColor = (c) => isDark ? `${c}.900` : `${c}.50`;
    const getTextColor = (c) => isDark ? `${c}.200` : `${c}.700`;
    const getBorderColor = (c) => isDark ? `${c}.700` : `${c}.200`;

    if (!day) return null;
    const holiday      = getHolidayForDate(day);
    const isWeekendDay = isWeekend(day);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside" motionPreset="slideInBottom">
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
            <ModalContent bg={cardBg} borderRadius="2xl" boxShadow="2xl" mx={4}>
                <Box h="3px" bgGradient="linear(to-r, teal.400, blue.500)" borderTopRadius="2xl" />
                <ModalHeader borderBottomWidth="1px" borderColor={border} pb={4}>
                    <HStack spacing={3}>
                        <Circle size="38px" bg={iconBg}>
                            <Icon as={MdCalendarToday} color="blue.500" boxSize={5} />
                        </Circle>
                        <VStack align="start" spacing={1}>
                            <Heading size="md" color={headingC}>{format(day, 'EEEE, MMMM d')}</Heading>
                            <HStack spacing={2} flexWrap="wrap">
                                {holiday && (
                                    <Badge colorScheme="yellow" variant="subtle" borderRadius="full" px={3}>
                                        <HStack spacing={1}><Icon as={MdStar} boxSize={3} /><Text fontSize="xs">{holiday.name}</Text></HStack>
                                    </Badge>
                                )}
                                {isWeekendDay && <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={3}>Weekend</Badge>}
                                <Badge colorScheme="blue" borderRadius="full" px={3}>{events.length} Event{events.length !== 1 ? 's' : ''}</Badge>
                            </HStack>
                        </VStack>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton borderRadius="xl" />
                <ModalBody py={5}>
                    <VStack spacing={4} align="stretch">
                        {holiday && (
                            <Alert status="info" borderRadius="xl">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle fontSize="sm" fontWeight="700">{holiday.name}</AlertTitle>
                                    <AlertDescription fontSize="xs" color={mutedC}>Federal Holiday — {holiday.type}</AlertDescription>
                                </Box>
                            </Alert>
                        )}
                        {events.length > 0 ? (
                            <VStack spacing={3} align="stretch">
                                <HStack spacing={2}>
                                    <Icon as={MdTimeline} color="blue.500" boxSize={4} />
                                    <Text fontSize="sm" fontWeight="700" color={headingC}>Scheduled Events</Text>
                                </HStack>
                                <MotionVStack spacing={2.5} align="stretch"
                                    variants={{ animate: { transition: { staggerChildren: 0.06 } } }}
                                    initial="initial" animate="animate"
                                >
                                    {events.map((event, idx) => {
                                        const cfg    = getECfg(event.extendedProps?.type);
                                        // AVOIDED INLINE HOOKS
                                        const chipBg = getBgColor(cfg.color);
                                        const chipC  = getTextColor(cfg.color);
                                        const chipBd = getBorderColor(cfg.color);
                                        return (
                                            <MotionBox
                                                key={event.id}
                                                variants={{
                                                    initial: { opacity: 0, x: -16 },
                                                    animate: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 260, damping: 26 } }
                                                }}
                                                whileHover={{ x: 4, transition: { duration: 0.14 } }}
                                            >
                                                <Box
                                                    bg={chipBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={chipBd}
                                                    cursor="pointer" onClick={() => onEventClick(null, event)}
                                                    _hover={{ boxShadow: 'md' }} transition="all 0.15s"
                                                >
                                                    <Flex justify="space-between" align="flex-start" mb={2}>
                                                        <HStack spacing={2}>
                                                            <Circle size="28px" bg={`${cfg.color}.100`}>
                                                                <Icon as={MdEvent} color={`${cfg.color}.500`} boxSize={3.5} />
                                                            </Circle>
                                                            <Text fontWeight="700" color={chipC} fontSize="sm">{event.title}</Text>
                                                        </HStack>
                                                        <Badge colorScheme={cfg.color} borderRadius="full" fontSize="2xs" px={2.5}>{cfg.label}</Badge>
                                                    </Flex>
                                                    {event.description && (
                                                        <Text fontSize="xs" color={mutedC} noOfLines={2} mb={2} lineHeight="1.6">{event.description}</Text>
                                                    )}
                                                    <HStack spacing={1} fontSize="xs" color={mutedC}>
                                                        <Icon as={MdAccessTime} boxSize={3.5} />
                                                        <Text fontWeight="600">{format(parseISO(event.start), 'h:mm a')}</Text>
                                                    </HStack>
                                                </Box>
                                            </MotionBox>
                                        );
                                    })}
                                </MotionVStack>
                            </VStack>
                        ) : (
                            <Flex direction="column" align="center" justify="center"
                                py={10} gap={3} bg={filterBg} borderRadius="2xl"
                            >
                                <MotionBox animate={float}>
                                    <Circle size="64px" bg="blue.50">
                                        <Icon as={MdEvent} boxSize={7} color="blue.400" />
                                    </Circle>
                                </MotionBox>
                                <VStack spacing={1} textAlign="center">
                                    <Text fontWeight="700" fontSize="md" color={headingC}>No events scheduled</Text>
                                    <Text fontSize="sm" color={mutedC}>This day is clear — add an event below</Text>
                                </VStack>
                            </Flex>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter borderTopWidth="1px" borderColor={border} gap={2}>
                    <Button variant="ghost" onClick={onClose} borderRadius="xl" size="sm">Close</Button>
                    <MotionBox whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                        <Button colorScheme="blue" leftIcon={<Icon as={MdAdd} />}
                            onClick={() => { onClose(); onCreateEvent(day); }}
                            borderRadius="xl" fontWeight="700"
                            boxShadow="0 4px 14px rgba(66,153,225,0.35)"
                        >
                            Add Event
                        </Button>
                    </MotionBox>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
});

// ─────────────────────────────────────────────
// MONTH NAVIGATOR
// ─────────────────────────────────────────────
const MonthNavigator = memo(({ currentDate, onPrev, onNext, onToday, isLoading }) => {
    const headingC = useColorModeValue('gray.800', 'white');
    const mutedC   = useColorModeValue('gray.500', 'gray.400');
    const isThisMonth = isSameDay(startOfMonth(currentDate), startOfMonth(new Date()));

    return (
        <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
            <HStack spacing={3}>
                <MotionBox
                    key={format(currentDate, 'MMM-yyyy')}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                >
                    <VStack align="start" spacing={0.5}>
                        <Heading size="xl" color={headingC} fontWeight="800" lineHeight="1">
                            {format(currentDate, 'MMMM')}
                        </Heading>
                        <Text fontSize="sm" color={mutedC} fontWeight="600" letterSpacing="0.05em">
                            {format(currentDate, 'yyyy')}
                        </Text>
                    </VStack>
                </MotionBox>
                {isLoading && (
                    <MotionBox animate={breathe}>
                        <HStack spacing={1.5}>
                            <PulseDot color="blue" size="6px" />
                            <Text fontSize="xs" color="blue.500" fontWeight="600">Updating...</Text>
                        </HStack>
                    </MotionBox>
                )}
            </HStack>
            <HStack spacing={2}>
                <AnimatePresence>
                    {!isThisMonth && (
                        <MotionBox initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}>
                            <Button size="sm" colorScheme="teal" variant="outline"
                                leftIcon={<Icon as={MdToday} boxSize={4} />}
                                onClick={onToday} borderRadius="xl" fontWeight="600"
                            >
                                Today
                            </Button>
                        </MotionBox>
                    )}
                </AnimatePresence>
                <HStack spacing={1}>
                    <MotionBox whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}>
                        <IconButton aria-label="Previous month" icon={<Icon as={MdChevronLeft} boxSize={5} />}
                            size="sm" variant="ghost" onClick={onPrev} borderRadius="xl" />
                    </MotionBox>
                    <MotionBox whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}>
                        <IconButton aria-label="Next month" icon={<Icon as={MdChevronRight} boxSize={5} />}
                            size="sm" variant="ghost" onClick={onNext} borderRadius="xl" />
                    </MotionBox>
                </HStack>
            </HStack>
        </Flex>
    );
});

// ─────────────────────────────────────────────
// FILTER PANEL
// ─────────────────────────────────────────────
const FilterPanel = memo(({
    selectedTechnician, setSelectedTechnician,
    selectedTeam,       setSelectedTeam,
    technicians, teams,
    inputBg, borderColor, textColor
}) => {
    const headingC = useColorModeValue('gray.800', 'white');
    const mutedC   = useColorModeValue('gray.500', 'gray.400');
    const cardBg   = useColorModeValue('white', 'gray.800');
    const iconBg   = useColorModeValue('blue.50', 'blue.900');
    const shadow   = useColorModeValue('0 4px 24px rgba(0,0,0,0.05)', '0 4px 24px rgba(0,0,0,0.3)');
    const hasFilters  = selectedTechnician || selectedTeam;
    const [show, setShow] = useState(true);

    return (
        <MotionBox variants={slideUp}>
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}
                boxShadow={shadow} overflow="hidden" mb={5}
            >
                <Box px={5} py={3.5} borderBottomWidth={show ? '1px' : '0'} borderColor={borderColor}>
                    <Flex justify="space-between" align="center">
                        <HStack spacing={3}>
                            <Circle size="32px" bg={iconBg}>
                                <Icon as={hasFilters ? MdTune : MdFilterList} boxSize={4} color="blue.500" />
                            </Circle>
                            <HStack spacing={2}>
                                <Text fontWeight="700" fontSize="sm" color={headingC}>Filters</Text>
                                {hasFilters && (
                                    <MotionBadge colorScheme="blue" borderRadius="full" fontSize="xs" px={2}
                                        initial={{ scale: 0 }} animate={{ scale: 1 }}>
                                        Active
                                    </MotionBadge>
                                )}
                            </HStack>
                        </HStack>
                        <HStack spacing={2}>
                            {hasFilters && (
                                <MotionBox initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}>
                                    <Button size="sm" variant="ghost" colorScheme="red"
                                        leftIcon={<Icon as={MdClose} boxSize={3.5} />}
                                        onClick={() => { setSelectedTechnician(''); setSelectedTeam(''); }}
                                        borderRadius="xl" fontSize="xs"
                                    >
                                        Clear
                                    </Button>
                                </MotionBox>
                            )}
                            <IconButton size="sm" variant="ghost"
                                icon={<Icon as={show ? MdKeyboardArrowUp : MdKeyboardArrowDown} />}
                                onClick={() => setShow(v => !v)}
                                aria-label="Toggle filters" borderRadius="xl"
                            />
                        </HStack>
                    </Flex>
                </Box>
                <Collapse in={show} animateOpacity>
                    <Box p={5}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                    letterSpacing="0.06em" color={mutedC} mb={1.5}
                                >
                                    <HStack spacing={1.5}><Icon as={MdPerson} boxSize={3.5} /><Text>Technician</Text></HStack>
                                </FormLabel>
                                <Select
                                    placeholder="All Technicians" value={selectedTechnician}
                                    onChange={(e) => { setSelectedTechnician(e.target.value); setSelectedTeam(''); }}
                                    bg={inputBg} size="sm" borderRadius="xl"
                                    borderColor={selectedTechnician ? 'blue.400' : borderColor}
                                    _focus={{ borderColor: 'blue.400' }} _hover={{ borderColor: 'blue.300' }}
                                >
                                    {technicians.map(t => <option key={t.id} value={t.id}>{t.username}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                    letterSpacing="0.06em" color={mutedC} mb={1.5}
                                >
                                    <HStack spacing={1.5}><Icon as={MdPeople} boxSize={3.5} /><Text>Team</Text></HStack>
                                </FormLabel>
                                <Select
                                    placeholder="All Teams" value={selectedTeam}
                                    onChange={(e) => { setSelectedTeam(e.target.value); setSelectedTechnician(''); }}
                                    bg={inputBg} size="sm" borderRadius="xl"
                                    borderColor={selectedTeam ? 'blue.400' : borderColor}
                                    _focus={{ borderColor: 'blue.400' }} _hover={{ borderColor: 'blue.300' }}
                                >
                                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </Select>
                            </FormControl>
                        </SimpleGrid>
                    </Box>
                </Collapse>
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────
// CREATE EVENT MODAL
// ─────────────────────────────────────────────
const CreateEventModal = memo(({
    isOpen, onClose, selectedDate,
    newEventTitle, setNewEventTitle,
    newEventDesc, setNewEventDesc,
    newEventStartTime, setNewEventStartTime,
    newEventEndTime, setNewEventEndTime,
    newEventLocation, setNewEventLocation,
    newEventPriority, setNewEventPriority,
    newEventCategory, setNewEventCategory,
    newEventAttendees, setNewEventAttendees,
    newEventReminder, setNewEventReminder,
    newEventRecurring, setNewEventRecurring,
    newEventNotes, setNewEventNotes,
    onSubmit, isSubmitting, onReset,
    technicians, borderColor, inputBg
}) => {
    const cardBg   = useColorModeValue('white', 'gray.800');
    const headingC = useColorModeValue('gray.800', 'white');
    const mutedC   = useColorModeValue('gray.500', 'gray.400');
    const tabBg    = useColorModeValue('gray.50', 'gray.750');
    const iconBg   = useColorModeValue('blue.50', 'blue.900');

    return (
        <Modal isOpen={isOpen} onClose={() => { onClose(); onReset(); }}
            size="2xl" isCentered scrollBehavior="inside" motionPreset="slideInBottom"
        >
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
            <ModalContent bg={cardBg} borderRadius="2xl" boxShadow="2xl" mx={4}>
                <Box h="3px" bgGradient="linear(to-r, blue.400, purple.500)" borderTopRadius="2xl" />
                <ModalHeader borderBottomWidth="1px" borderColor={borderColor} pb={4}>
                    <HStack spacing={3}>
                        <Circle size="38px" bg={iconBg}>
                            <Icon as={MdAdd} color="blue.500" boxSize={5} />
                        </Circle>
                        <VStack align="start" spacing={0.5}>
                            <Heading size="md" color={headingC}>Create Event</Heading>
                            {selectedDate && (
                                <Text fontSize="xs" color={mutedC} fontWeight="600">
                                    {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                                </Text>
                            )}
                        </VStack>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton borderRadius="xl" />
                <ModalBody py={5}>
                    <Tabs colorScheme="blue" variant="unstyled">
                        <Box bg={tabBg} borderRadius="xl" p={1.5} mb={5}>
                            <TabList gap={1}>
                                {[
                                    { icon: MdInfoOutline, label: 'Basic Info' },
                                    { icon: MdLabel,       label: 'Details'    },
                                    { icon: MdAlarm,       label: 'Reminders'  },
                                ].map(({ icon, label }) => (
                                    <Tab key={label} flex="1" borderRadius="xl" py={2}
                                        fontSize="sm" fontWeight="600" color={mutedC}
                                        _selected={{ bg: cardBg, color: headingC, boxShadow: 'sm' }}
                                        transition="all 0.18s" _hover={{ color: headingC }}
                                    >
                                        <HStack spacing={2} justify="center">
                                            <Icon as={icon} boxSize={4} />
                                            <Text>{label}</Text>
                                        </HStack>
                                    </Tab>
                                ))}
                            </TabList>
                        </Box>

                        <TabPanels>
                            {/* TAB 1: BASIC */}
                            <TabPanel p={0}>
                                <VStack spacing={4} align="stretch">
                                    <FormControl isRequired>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={1.5}>Event Title</FormLabel>
                                        <Input
                                            placeholder="e.g., Team Meeting, System Maintenance"
                                            value={newEventTitle} onChange={(e) => setNewEventTitle(e.target.value)}
                                            bg={inputBg} borderRadius="xl"
                                            _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
                                            _hover={{ borderColor: 'blue.300' }}
                                        />
                                        <FormHelperText fontSize="xs">Give your event a clear, descriptive title</FormHelperText>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={1.5}>Description</FormLabel>
                                        <Textarea
                                            placeholder="Add details about this event..."
                                            value={newEventDesc} onChange={(e) => setNewEventDesc(e.target.value)}
                                            rows={4} bg={inputBg} borderRadius="xl"
                                            _focus={{ borderColor: 'blue.400' }} _hover={{ borderColor: 'blue.300' }}
                                        />
                                    </FormControl>
                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                                letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                                <HStack spacing={1}><Icon as={MdAccessTime} boxSize={3.5} /><Text>Start Time</Text></HStack>
                                            </FormLabel>
                                            <Input type="time" value={newEventStartTime}
                                                onChange={(e) => setNewEventStartTime(e.target.value)}
                                                bg={inputBg} borderRadius="xl" _focus={{ borderColor: 'blue.400' }}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                                letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                                <HStack spacing={1}><Icon as={MdAccessTime} boxSize={3.5} /><Text>End Time</Text></HStack>
                                            </FormLabel>
                                            <Input type="time" value={newEventEndTime}
                                                onChange={(e) => setNewEventEndTime(e.target.value)}
                                                bg={inputBg} borderRadius="xl" _focus={{ borderColor: 'blue.400' }}
                                            />
                                        </FormControl>
                                    </SimpleGrid>
                                    <Checkbox colorScheme="blue"
                                        onChange={(e) => {
                                            if (e.target.checked) { setNewEventStartTime('00:00'); setNewEventEndTime('23:59'); }
                                            else { setNewEventStartTime('09:00'); setNewEventEndTime('10:00'); }
                                        }}
                                    >
                                        <Text fontSize="sm" fontWeight="600">All Day Event</Text>
                                    </Checkbox>
                                </VStack>
                            </TabPanel>

                            {/* TAB 2: DETAILS */}
                            <TabPanel p={0}>
                                <VStack spacing={4} align="stretch">
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                            <HStack spacing={1}><Icon as={MdLocationOn} boxSize={3.5} /><Text>Location</Text></HStack>
                                        </FormLabel>
                                        <Input
                                            placeholder="e.g., Conference Room A, Building 2"
                                            value={newEventLocation} onChange={(e) => setNewEventLocation(e.target.value)}
                                            bg={inputBg} borderRadius="xl" _focus={{ borderColor: 'blue.400' }} _hover={{ borderColor: 'blue.300' }}
                                        />
                                        <FormHelperText fontSize="xs">Where will this event take place?</FormHelperText>
                                    </FormControl>
                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                                letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                                <HStack spacing={1}><Icon as={MdWarning} boxSize={3.5} /><Text>Priority</Text></HStack>
                                            </FormLabel>
                                            <Select value={newEventPriority}
                                                onChange={(e) => setNewEventPriority(e.target.value)}
                                                bg={inputBg} size="sm" borderRadius="xl" _focus={{ borderColor: 'blue.400' }}
                                            >
                                                <option value="low">🟢 Low</option>
                                                <option value="medium">🟡 Medium</option>
                                                <option value="high">🟠 High</option>
                                                <option value="critical">🔴 Critical</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                                letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                                <HStack spacing={1}><Icon as={MdLabel} boxSize={3.5} /><Text>Category</Text></HStack>
                                            </FormLabel>
                                            <Select value={newEventCategory}
                                                onChange={(e) => setNewEventCategory(e.target.value)}
                                                bg={inputBg} size="sm" borderRadius="xl" _focus={{ borderColor: 'blue.400' }}
                                            >
                                                <option value="general">General</option>
                                                <option value="meeting">Meeting</option>
                                                <option value="training">Training</option>
                                                <option value="maintenance">Maintenance</option>
                                                <option value="inspection">Inspection</option>
                                                <option value="review">Review</option>
                                                <option value="planning">Planning</option>
                                                <option value="social">Social</option>
                                            </Select>
                                        </FormControl>
                                    </SimpleGrid>
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                            <HStack spacing={1}><Icon as={MdPeople} boxSize={3.5} /><Text>Attendees</Text></HStack>
                                        </FormLabel>
                                        <CheckboxGroup colorScheme="blue"
                                            value={newEventAttendees} onChange={(vals) => setNewEventAttendees(vals)}
                                        >
                                            <Stack spacing={2} maxH="180px" overflowY="auto" p={3}
                                                borderWidth="1px" borderRadius="xl" borderColor={borderColor} bg={inputBg}
                                            >
                                                {technicians.slice(0, 10).map(t => (
                                                    <Checkbox key={t.id} value={t.id.toString()}>
                                                        <HStack spacing={2}>
                                                            <Avatar size="xs" name={t.username} />
                                                            <Text fontSize="sm" fontWeight="500">{t.username}</Text>
                                                        </HStack>
                                                    </Checkbox>
                                                ))}
                                            </Stack>
                                        </CheckboxGroup>
                                        <FormHelperText fontSize="xs">Select team members to invite</FormHelperText>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={2}>Event Color</FormLabel>
                                        <Wrap spacing={2}>
                                            {['blue', 'green', 'purple', 'red', 'orange', 'teal', 'pink', 'cyan'].map(c => (
                                                <WrapItem key={c}>
                                                    <MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
                                                        <Box w="36px" h="36px" bg={`${c}.500`} borderRadius="xl"
                                                            cursor="pointer" borderWidth="2px" borderColor="transparent"
                                                            _hover={{ borderColor: `${c}.700` }} transition="all 0.15s"
                                                        />
                                                    </MotionBox>
                                                </WrapItem>
                                            ))}
                                        </Wrap>
                                    </FormControl>
                                </VStack>
                            </TabPanel>

                            {/* TAB 3: REMINDERS */}
                            <TabPanel p={0}>
                                <VStack spacing={5} align="stretch">
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                            <HStack spacing={1}><Icon as={MdAlarm} boxSize={3.5} /><Text>Reminder</Text></HStack>
                                        </FormLabel>
                                        <Select value={newEventReminder}
                                            onChange={(e) => setNewEventReminder(e.target.value)}
                                            bg={inputBg} size="sm" borderRadius="xl" _focus={{ borderColor: 'blue.400' }}
                                        >
                                            <option value="none">No reminder</option>
                                            <option value="15">15 minutes before</option>
                                            <option value="30">30 minutes before</option>
                                            <option value="60">1 hour before</option>
                                            <option value="1440">1 day before</option>
                                        </Select>
                                        <FormHelperText fontSize="xs">When should you be reminded?</FormHelperText>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={2}>
                                            <HStack spacing={1}><Icon as={MdRepeat} boxSize={3.5} /><Text>Recurring Event</Text></HStack>
                                        </FormLabel>
                                        <RadioGroup value={newEventRecurring} onChange={setNewEventRecurring}>
                                            <SimpleGrid columns={2} spacing={2}>
                                                {[
                                                    { value: 'none',    label: '🚫 No repeat' },
                                                    { value: 'daily',   label: '📅 Daily'     },
                                                    { value: 'weekly',  label: '📆 Weekly'    },
                                                    { value: 'monthly', label: '🗓 Monthly'   },
                                                    { value: 'yearly',  label: '📋 Yearly'    },
                                                ].map(({ value, label }) => (
                                                    <Radio key={value} value={value} colorScheme="blue" size="sm">
                                                        <Text fontSize="sm" fontWeight="500">{label}</Text>
                                                    </Radio>
                                                ))}
                                            </SimpleGrid>
                                        </RadioGroup>
                                    </FormControl>
                                    <AnimatePresence>
                                        {newEventRecurring !== 'none' && (
                                            <MotionBox
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                            >
                                                <FormControl>
                                                    <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                                        letterSpacing="0.06em" color={mutedC} mb={1.5}>
                                                        End After (occurrences)
                                                    </FormLabel>
                                                    <NumberInput min={1} max={365} defaultValue={10}
                                                        bg={inputBg} borderRadius="xl" size="sm"
                                                    >
                                                        <NumberInputField borderRadius="xl" />
                                                        <NumberInputStepper>
                                                            <NumberIncrementStepper />
                                                            <NumberDecrementStepper />
                                                        </NumberInputStepper>
                                                    </NumberInput>
                                                    <FormHelperText fontSize="xs">How many times should this event repeat?</FormHelperText>
                                                </FormControl>
                                            </MotionBox>
                                        )}
                                    </AnimatePresence>
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase"
                                            letterSpacing="0.06em" color={mutedC} mb={1.5}>Additional Notes</FormLabel>
                                        <Textarea
                                            placeholder="Any additional information or special instructions..."
                                            value={newEventNotes} onChange={(e) => setNewEventNotes(e.target.value)}
                                            rows={4} bg={inputBg} borderRadius="xl"
                                            _focus={{ borderColor: 'blue.400' }} _hover={{ borderColor: 'blue.300' }}
                                        />
                                    </FormControl>
                                </VStack>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter borderTopWidth="1px" borderColor={borderColor} gap={2}>
                    <Button variant="ghost" size="sm" onClick={() => { onClose(); onReset(); }} borderRadius="xl">Cancel</Button>
                    <MotionBox whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                        <Button colorScheme="blue" onClick={onSubmit}
                            isLoading={isSubmitting} loadingText="Creating..."
                            leftIcon={<Icon as={MdCheckCircle} />} borderRadius="xl"
                            boxShadow="0 4px 14px rgba(66,153,225,0.35)" fontWeight="700"
                            isDisabled={!newEventTitle}
                        >
                            Create Event
                        </Button>
                    </MotionBox>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
});

// ─────────────────────────────────────────────
// UPCOMING EVENTS LIST
// ─────────────────────────────────────────────
const UpcomingEventsList = memo(({ allItems, onEventClick, headingColor, mutedColor, cardBg, borderColor }) => {
    // 1. Get the current color mode state safely
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const iconBg   = useColorModeValue('blue.50', 'blue.900');
    const shadow   = useColorModeValue('0 4px 24px rgba(0,0,0,0.05)', '0 4px 24px rgba(0,0,0,0.3)');

    // 2. Pure JS functions (No hooks inside!)
    const getBgColor = (c) => isDark ? `${c}.900` : `${c}.50`;
    const getTextColor = (c) => isDark ? `${c}.200` : `${c}.700`;
    const getBorderColor = (c) => isDark ? `${c}.700` : `${c}.200`;

    const upcoming = useMemo(() => {
        const today = startOfDay(new Date());
        return allItems
            .filter(item => {
                try { return isAfter(parseISO(item.start), today) || isSameDay(parseISO(item.start), today); }
                catch { return false; }
            })
            .sort((a, b) => parseISO(a.start) - parseISO(b.start))
            .slice(0, 8);
    }, [allItems]);

    if (upcoming.length === 0) return null;

    return (
        <MotionBox variants={slideUp} mt={5}>
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}
                boxShadow={shadow} overflow="hidden"
            >
                <Box px={5} py={4} borderBottomWidth="1px" borderColor={borderColor}>
                    <HStack spacing={3}>
                        <Circle size="32px" bg={iconBg}>
                            <Icon as={MdSchedule} boxSize={4} color="blue.500" />
                        </Circle>
                        <VStack align="start" spacing={0.5}>
                            <Text fontWeight="700" fontSize="sm" color={headingColor}>Upcoming Events</Text>
                            <Text fontSize="xs" color={mutedColor}>{upcoming.length} scheduled</Text>
                        </VStack>
                    </HStack>
                </Box>
                <VStack spacing={0} align="stretch" p={3}>
                    <MotionVStack spacing={1.5} align="stretch"
                        variants={{ animate: { transition: { staggerChildren: 0.05 } } }}
                        initial="initial" animate="animate"
                    >
                        {upcoming.map((event) => {
                            const cfg      = getECfg(event.extendedProps?.type);
                            // AVOIDED INLINE HOOKS
                            const chipBg   = getBgColor(cfg.color);
                            const chipC    = getTextColor(cfg.color);
                            const chipBd   = getBorderColor(cfg.color);
                            let dateLabel;
                            try {
                                const d = parseISO(event.start);
                                dateLabel = isSameDay(d, new Date())
                                    ? `Today · ${format(d, 'h:mm a')}`
                                    : format(d, 'MMM d · h:mm a');
                            } catch { dateLabel = '—'; }

                            return (
                                <MotionBox
                                    key={event.id}
                                    variants={{
                                        initial: { opacity: 0, x: -12 },
                                        animate: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 280, damping: 26 } }
                                    }}
                                    whileHover={{ x: 3, transition: { duration: 0.14 } }}
                                >
                                    <HStack spacing={3} p={2.5} bg={chipBg} borderRadius="xl"
                                        borderWidth="1px" borderColor={chipBd}
                                        cursor="pointer" onClick={() => onEventClick(null, event)}
                                    >
                                        <Box w="3px" h="32px" borderRadius="full" bg={`${cfg.color}.400`} flexShrink={0} />
                                        <VStack align="start" spacing={0} flex="1" minW="0">
                                            <Text fontSize="xs" fontWeight="700" color={chipC} noOfLines={1}>{event.title}</Text>
                                            <Text fontSize="2xs" color={mutedColor} fontWeight="500">{dateLabel}</Text>
                                        </VStack>
                                        <Badge colorScheme={cfg.color} fontSize="2xs" borderRadius="full" px={2} flexShrink={0}>
                                            {cfg.label}
                                        </Badge>
                                    </HStack>
                                </MotionBox>
                            );
                        })}
                    </MotionVStack>
                </VStack>
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────
// PAGE HEADER
// ─────────────────────────────────────────────
const PageHeader = memo(({ isLoading, autoRefresh, setAutoRefresh, refreshInterval, onRefresh, onExport, onNewEvent, headingColor, mutedColor, borderColor }) => {
    const cardBg  = useColorModeValue('white', 'gray.800');
    const iconBg  = useColorModeValue('blue.50', 'blue.900');
    const textC   = useColorModeValue('gray.700', 'gray.300');
    const shadow  = useColorModeValue('0 4px 24px rgba(0,0,0,0.06)', '0 4px 24px rgba(0,0,0,0.3)');

    return (
        <MotionBox variants={slideUp} mb={6}>
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}
                boxShadow={shadow} overflow="hidden" position="relative"
            >
                <MotionBox
                    h="4px"
                    bgGradient="linear(to-r, teal.400, blue.500, purple.400, teal.400)"
                    style={{ backgroundSize: '300% 100%' }}
                    animate={{ backgroundPosition: ['0% 0%', '100% 0%', '0% 0%'] }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                />
                <Box p={6}>
                    <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                        <HStack spacing={4}>
                            <MotionBox whileHover={{ scale: 1.08, rotate: 5 }} transition={{ type: 'spring', stiffness: 320 }}>
                                <Circle size="52px" bg={iconBg} boxShadow="0 4px 16px rgba(66,153,225,0.2)">
                                    <Icon as={MdCalendarToday} boxSize={7} color="blue.500" />
                                </Circle>
                            </MotionBox>
                            <VStack align="flex-start" spacing={1.5}>
                                <Heading as="h2" size="lg" color={headingColor} fontWeight="800" lineHeight="1">
                                    Operational Calendar
                                </Heading>
                                <HStack spacing={2} flexWrap="wrap">
                                    <Text fontSize="xs" color={mutedColor}>
                                        Unified view of events, work orders & maintenance
                                    </Text>
                                    {autoRefresh && (
                                        <HStack spacing={1.5}>
                                            <PulseDot color="green" size="6px" />
                                            <Text fontSize="xs" color="green.500" fontWeight="600">Live</Text>
                                        </HStack>
                                    )}
                                </HStack>
                            </VStack>
                        </HStack>
                        <HStack spacing={2} flexWrap="wrap">
                            <FormControl display="flex" alignItems="center" w="auto" gap={2}>
                                <FormLabel htmlFor="cal-auto-refresh" mb="0" fontSize="sm" fontWeight="600" color={textC}>Auto</FormLabel>
                                <Switch id="cal-auto-refresh" isChecked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    colorScheme="green" size="sm"
                                />
                            </FormControl>
                            <Tooltip label="Refresh calendar" hasArrow>
                                <IconButton icon={<Icon as={MdRefresh} />} size="sm" variant="ghost"
                                    colorScheme="blue" onClick={onRefresh} isLoading={isLoading}
                                    aria-label="Refresh" borderRadius="xl"
                                />
                            </Tooltip>
                            <Tooltip label="Export to CSV" hasArrow>
                                <IconButton icon={<Icon as={MdFileDownload} />} size="sm" variant="ghost"
                                    onClick={onExport} aria-label="Export" borderRadius="xl" />
                            </Tooltip>
                            <Tooltip label="Print calendar" hasArrow>
                                <IconButton icon={<Icon as={MdPrint} />} size="sm" variant="ghost"
                                    onClick={printCalendar} aria-label="Print" borderRadius="xl" />
                            </Tooltip>
                            <MotionBox whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                                <Button leftIcon={<Icon as={MdAdd} />} colorScheme="blue" size="sm"
                                    onClick={onNewEvent} borderRadius="xl" fontWeight="700"
                                    boxShadow="0 4px 14px rgba(66,153,225,0.3)"
                                >
                                    New Event
                                </Button>
                            </MotionBox>
                        </HStack>
                    </Flex>
                </Box>
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
function CalendarPage() {
    // THEME
    const bgColor           = useColorModeValue('gray.50', 'gray.900');
    const cardBg            = useColorModeValue('white', 'gray.800');
    const textColor         = useColorModeValue('gray.700', 'gray.200');
    const mutedColor        = useColorModeValue('gray.500', 'gray.400');
    const borderColor       = useColorModeValue('gray.100', 'gray.700');
    const headingColor      = useColorModeValue('gray.800', 'white');
    const cellBorderColor   = useColorModeValue('gray.100', 'gray.700');
    const todayBg           = useColorModeValue('teal.50',    'teal.900');
    const todayBorderColor  = useColorModeValue('teal.300',   'teal.600');
    const holidayBg         = useColorModeValue('yellow.50',  'yellow.900');
    const holidayBorderColor = useColorModeValue('yellow.200', 'yellow.700');
    const weekendBg         = useColorModeValue('gray.25',    'gray.850');
    const cellHoverBg       = useColorModeValue('gray.50',    'gray.700');
    const inputBg           = useColorModeValue('white',      'gray.700');
    const tableHeaderBg     = useColorModeValue('gray.50',    'gray.750');

    // STATE
    const [currentDate,             setCurrentDate]             = useState(new Date());
    const [allItems,                setAllItems]                = useState([]);
    const [selectedDate,            setSelectedDate]            = useState(null);
    const [selectedDayForDetails,  setSelectedDayForDetails]  = useState(null);
    const [selectedWorkOrder,       setSelectedWorkOrder]       = useState(null);

    const [newEventTitle,        setNewEventTitle]        = useState('');
    const [newEventDesc,         setNewEventDesc]         = useState('');
    const [newEventStartTime,    setNewEventStartTime]    = useState('09:00');
    const [newEventEndTime,      setNewEventEndTime]      = useState('10:00');
    const [newEventLocation,     setNewEventLocation]     = useState('');
    const [newEventPriority,     setNewEventPriority]     = useState('medium');
    const [newEventCategory,     setNewEventCategory]     = useState('general');
    const [newEventAttendees,    setNewEventAttendees]    = useState([]);
    const [newEventReminder,     setNewEventReminder]     = useState('15');
    const [newEventRecurring,    setNewEventRecurring]    = useState('none');
    const [newEventNotes,        setNewEventNotes]        = useState('');

    const [technicians, setTechnicians] = useState([]);
    const [teams,       setTeams]       = useState([]);
    const [selectedTechnician, setSelectedTechnician] = useState('');
    const [selectedTeam,       setSelectedTeam]       = useState('');

    const debouncedTechnician = useDebounce(selectedTechnician, 300);
    const debouncedTeam       = useDebounce(selectedTeam,       300);

    const [autoRefresh,     setAutoRefresh]     = useLocalStorage('calendar-auto-refresh',     false);
    const [refreshInterval]                     = useLocalStorage('calendar-refresh-interval', 30000);
    const [itemToDelete,    setItemToDelete]    = useState(null);
    const [isSubmitting,    setIsSubmitting]    = useState(false);

    const toast = useToast();

    const { isOpen: isCreateModalOpen,   onOpen: onCreateModalOpen,   onClose: onCreateModalClose   } = useDisclosure();
    const { isOpen: isDayDetailsOpen,    onOpen: onDayDetailsOpen,    onClose: onDayDetailsClose    } = useDisclosure();
    const { isOpen: isWorkOrderModalOpen, onOpen: onWorkOrderModalOpen, onClose: onWorkOrderModalClose } = useDisclosure();
    const { isOpen: isDeleteConfirmOpen, onOpen: onDeleteConfirmOpen, onClose: onDeleteConfirmClose } = useDisclosure();

    // DATA LOADING
    const asyncLoadData = useCallback(async () => {
        const token = getAuthToken();
        if (!token) throw new Error("Authentication is required.");
        console.log(`[CalendarPage] Loading unified calendar data with filters...`);
        const data = await fetchUnifiedCalendarData(token, { technicianId: debouncedTechnician, teamId: debouncedTeam });
        setAllItems(data || []);
        console.log(`[CalendarPage] Data loaded: ${data?.length} total items`);
        toast({ title: "Calendar Updated", description: `Loaded ${data?.length || 0} events`, status: "success", duration: 2000, isClosable: true });
    }, [debouncedTechnician, debouncedTeam, toast]);

    const { loading: isLoading, error, execute: loadData } = useAsync(asyncLoadData);

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const techResponse = await fetchUsers({ role: 'tech' });
                const teamResponse = await fetchTeams();
                setTechnicians(techResponse.data || []);
                setTeams(teamResponse || []);
            } catch (err) {
                toast({ title: "Could not load filter options", description: err.message, status: "error" });
            }
        };
        fetchFilters();
    }, [toast]);

    useEffect(() => { loadData(); }, [currentDate, debouncedTechnician, debouncedTeam, loadData]);

    useInterval(() => {
        if (autoRefresh) { console.log('[CalendarPage] Auto-refreshing...'); loadData(); }
    }, autoRefresh ? refreshInterval : null);

    // CALENDAR CALCULATIONS
    const monthStart      = startOfMonth(currentDate);
    const daysInMonthGrid = eachDayOfInterval({
        start: startOfWeek(monthStart),
        end:   endOfWeek(endOfMonth(monthStart))
    });

    const itemsByDate = useMemo(() => {
        const grouped = {};
        if (Array.isArray(allItems)) {
            allItems.forEach(item => {
                if (item.start && item.title) {
                    try {
                        const d = parseISO(item.start);
                        if (isValid(d)) {
                            const key = format(d, 'yyyy-MM-dd');
                            if (!grouped[key]) grouped[key] = [];
                            grouped[key].push({ ...item, displayDate: d });
                        }
                    } catch (e) { console.warn("Error parsing item date:", item.start); }
                }
            });
        }
        Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.displayDate || 0) - (b.displayDate || 0)));
        return grouped;
    }, [allItems]);

    const stats = useMemo(() => ({
        total:      allItems.length,
        workOrders: allItems.filter(i => i.extendedProps?.type === 'work_order').length,
        maintenance: allItems.filter(i => i.extendedProps?.type === 'maintenance').length,
        manual:     allItems.filter(i => i.extendedProps?.type === 'manual_event').length,
    }), [allItems]);

    // HANDLERS
    const handlePrevMonth  = useCallback(() => setCurrentDate(prev => subMonths(prev, 1)), []);
    const handleNextMonth  = useCallback(() => setCurrentDate(prev => addMonths(prev, 1)), []);

    const resetEventForm   = useCallback(() => {
        setNewEventTitle(''); setNewEventDesc('');
        setNewEventStartTime('09:00'); setNewEventEndTime('10:00');
        setNewEventLocation(''); setNewEventPriority('medium');
        setNewEventCategory('general'); setNewEventAttendees([]);
        setNewEventReminder('15'); setNewEventRecurring('none'); setNewEventNotes('');
    }, []);

    const handleDayClick = useCallback((day) => {
        setSelectedDate(day); resetEventForm(); onCreateModalOpen();
    }, [onCreateModalOpen, resetEventForm]);

    const handleDayDetailsClick = useCallback((e, day) => {
        e.stopPropagation(); setSelectedDayForDetails(day); onDayDetailsOpen();
    }, [onDayDetailsOpen]);

    const handleItemClick = useCallback((e, item) => {
        if (e) e.stopPropagation();
        const { type, originalId } = item.extendedProps;
        if (type === 'work_order') { setSelectedWorkOrder({ id: originalId }); onWorkOrderModalOpen(); }
        else if (type === 'maintenance') { toast({ title: 'Maintenance Task', description: `Task ID: ${originalId}`, status: 'info' }); }
        else { toast({ title: 'Event', description: item.title, status: 'info' }); }
    }, [onWorkOrderModalOpen, toast]);

    const handleCreateEventSubmit = useCallback(async (e) => {
        if (e) e.preventDefault();
        if (!selectedDate || !newEventTitle) {
            toast({ title: "Error", description: "Title and date required.", status: "warning", duration: 3000 }); return;
        }
        setIsSubmitting(true);
        const token = getAuthToken();
        if (!token) { toast({ title: "Auth Error", status: "error" }); setIsSubmitting(false); return; }

        const startDT = new Date(selectedDate);
        const [sh, sm] = newEventStartTime.split(':');
        startDT.setHours(parseInt(sh), parseInt(sm));
        const endDT = new Date(selectedDate);
        const [eh, em] = newEventEndTime.split(':');
        endDT.setHours(parseInt(eh), parseInt(em));

        const eventData = {
            title: newEventTitle, description: newEventDesc,
            start_time: startDT.toISOString(), end_time: endDT.toISOString(),
            location: newEventLocation, priority: newEventPriority,
            category: newEventCategory, attendees: newEventAttendees,
            reminder: newEventReminder, recurring: newEventRecurring, notes: newEventNotes
        };

        const tempId    = `temp-${Date.now()}`;
        const tempEvent = { id: tempId, title: newEventTitle, start: startDT.toISOString(),
            extendedProps: { type: 'manual_event', originalId: tempId }, description: newEventDesc };

        setAllItems(prev => [...prev, tempEvent]);
        onCreateModalClose();

        try {
            const created = await createCalendarEvent(eventData, token);
            setAllItems(prev => prev.map(item =>
                item.id === tempId ? { ...created, extendedProps: { type: 'manual_event', originalId: created.id } } : item
            ));
            toast({ title: "Event Created", description: `${newEventTitle} has been scheduled`, status: "success", duration: 3000 });
        } catch (err) {
            setAllItems(prev => prev.filter(item => item.id !== tempId));
            toast({ title: "Error Creating Event", description: err.message, status: "error" });
        } finally { setIsSubmitting(false); resetEventForm(); }
    }, [
        selectedDate, newEventTitle, newEventDesc, newEventStartTime, newEventEndTime,
        newEventLocation, newEventPriority, newEventCategory, newEventAttendees,
        newEventReminder, newEventRecurring, newEventNotes,
        onCreateModalClose, resetEventForm, toast
    ]);

    const handleDeleteEventClick = useCallback((e, item) => {
        e.stopPropagation(); setItemToDelete(item); onDeleteConfirmOpen();
    }, [onDeleteConfirmOpen]);

    const confirmDelete = useCallback(async () => {
        if (!itemToDelete) return;
        const token = getAuthToken();
        if (!token) { toast({ title: "Auth Error", status: "error" }); return; }
        const itemId = itemToDelete.extendedProps.originalId;
        const prev   = [...allItems];
        setAllItems(p => p.filter(i => i.extendedProps.originalId !== itemId));
        onDeleteConfirmClose();
        try {
            await deleteCalendarEvent(itemId, token);
            toast({ title: "Event Deleted", status: "success", duration: 2000 });
        } catch (err) { setAllItems(prev); toast({ title: "Error Deleting Event", description: err.message, status: "error" }); }
        setItemToDelete(null);
    }, [itemToDelete, allItems, onDeleteConfirmClose, toast]);

    const handleExport = useCallback(() => {
        try { exportToCSV(allItems); toast({ title: "Calendar Exported", description: "Data exported to CSV", status: "success", duration: 2000 }); }
        catch (err) { toast({ title: "Export Failed", description: err.message, status: "error" }); }
    }, [allItems, toast]);

    const handleRefresh = useCallback(() => loadData(), [loadData]);

    // RENDER
    return (
        <ErrorBoundary>
            <MotionBox
                bg={bgColor} minH="100vh"
                pt={{ base: '130px', md: '80px', xl: '80px' }} pb={10}
                variants={pageVariants} initial="initial" animate="animate" exit="exit"
            >
                <Box maxW="1500px" mx="auto" px={{ base: 3, md: 5, lg: 8 }}>

                    <PageHeader
                        isLoading={isLoading} autoRefresh={autoRefresh}
                        setAutoRefresh={setAutoRefresh} refreshInterval={refreshInterval}
                        onRefresh={handleRefresh} onExport={handleExport}
                        onNewEvent={() => handleDayClick(new Date())}
                        headingColor={headingColor} mutedColor={mutedColor} borderColor={borderColor}
                    />

                    <MotionSimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}
                        variants={stagger} initial="initial" animate="animate"
                    >
                        <StatsCard label="Total Events"   value={stats.total}      icon={MdEvent}         color="blue"   subtitle="All calendar items"  isLoading={isLoading && allItems.length === 0} index={0} />
                        <StatsCard label="Work Orders"    value={stats.workOrders} icon={MdDashboard}     color="orange" subtitle="Active work orders"  isLoading={isLoading && allItems.length === 0} index={1} />
                        <StatsCard label="Maintenance"    value={stats.maintenance} icon={MdSettings}     color="green"  subtitle="Scheduled tasks"     isLoading={isLoading && allItems.length === 0} index={2} />
                        <StatsCard label="Manual Events"  value={stats.manual}     icon={MdNotifications} color="purple" subtitle="User-created events" isLoading={isLoading && allItems.length === 0} index={3} />
                    </MotionSimpleGrid>

                    <CalendarLegend />

                    <FilterPanel
                        selectedTechnician={selectedTechnician} setSelectedTechnician={setSelectedTechnician}
                        selectedTeam={selectedTeam}             setSelectedTeam={setSelectedTeam}
                        technicians={technicians} teams={teams}
                        inputBg={inputBg} borderColor={borderColor} textColor={textColor}
                    />

                    <DataStatus
                        isLoading={isLoading && allItems.length === 0}
                        error={error} data={allItems}
                        emptyState={{ title: 'No events scheduled', description: 'Click on a day to create your first event', icon: <Icon as={MdAdd} boxSize={12} /> }}
                        loadingText="Loading calendar..."
                    >
                        <LoadingOverlay isLoading={isLoading && allItems.length > 0} text="Updating...">
                            <MotionBox variants={slideUp}>
                                <Box bg={cardBg} p={{ base: 3, md: 5 }} borderRadius="2xl"
                                    borderWidth="1px" borderColor={borderColor} boxShadow="xl"
                                    overflow="hidden" position="relative"
                                >
                                    <Box position="absolute" top="0" left="0" right="0" h="3px"
                                        bgGradient="linear(to-r, teal.400, blue.500)" />

                                    <MonthNavigator
                                        currentDate={currentDate}
                                        onPrev={handlePrevMonth} onNext={handleNextMonth}
                                        onToday={() => setCurrentDate(new Date())}
                                        isLoading={isLoading && allItems.length > 0}
                                    />

                                    <SimpleGrid columns={7} spacing={1} mb={3}>
                                        {daysOfWeek.map((day, i) => (
                                            <Box key={day} py={2} textAlign="center" bg={tableHeaderBg} borderRadius="xl">
                                                <Text
                                                    fontSize="11px" fontWeight="800"
                                                    color={(i === 0 || i === 6) ? mutedColor : textColor}
                                                    textTransform="uppercase" letterSpacing="0.08em"
                                                >
                                                    {day}
                                                </Text>
                                            </Box>
                                        ))}
                                    </SimpleGrid>

                                    <MotionSimpleGrid columns={7} spacing={{ base: 0.5, md: 1 }}
                                        variants={stagger} initial="initial" animate="animate"
                                    >
                                        {daysInMonthGrid.map((day, index) => {
                                            const dayItems       = itemsByDate[format(day, 'yyyy-MM-dd')] || [];
                                            const isCurrentMonth = isSameMonth(day, currentDate);
                                            const isTodayCell    = isSameDay(day, new Date());
                                            const holiday        = getHolidayForDate(day);
                                            const isWeekendDay   = isWeekend(day);
                                            return (
                                                <CalendarCell
                                                    key={index} day={day} dayItems={dayItems}
                                                    isCurrentMonth={isCurrentMonth} isToday={isTodayCell}
                                                    holiday={holiday} isWeekendDay={isWeekendDay}
                                                    onDayClick={handleDayClick}
                                                    onDayDetailsClick={handleDayDetailsClick}
                                                    onItemClick={handleItemClick}
                                                    onDeleteClick={handleDeleteEventClick}
                                                    todayBg={todayBg} holidayBg={holidayBg}
                                                    weekendBg={weekendBg} cellHoverBg={cellHoverBg}
                                                    todayBorderColor={todayBorderColor}
                                                    holidayBorderColor={holidayBorderColor}
                                                    cellBorderColor={cellBorderColor}
                                                    textColor={textColor} mutedColor={mutedColor}
                                                    headingColor={headingColor}
                                                />
                                            );
                                        })}
                                    </MotionSimpleGrid>

                                    <Box mt={4} pt={3} borderTopWidth="1px" borderColor={borderColor}>
                                        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                                            <HStack spacing={3} flexWrap="wrap">
                                                <Text fontSize="xs" color={mutedColor}>
                                                    <strong>{daysInMonthGrid.filter(d => isSameMonth(d, currentDate)).length}</strong> days in {format(currentDate, 'MMMM')}
                                                </Text>
                                                {allItems.length > 0 && (
                                                    <Text fontSize="xs" color={mutedColor}>· <strong>{allItems.length}</strong> total events</Text>
                                                )}
                                            </HStack>
                                            <Text fontSize="xs" color={mutedColor}>Click to create · Double-click for details</Text>
                                        </Flex>
                                    </Box>
                                </Box>
                            </MotionBox>
                        </LoadingOverlay>
                    </DataStatus>

                    <UpcomingEventsList
                        allItems={allItems} onEventClick={handleItemClick}
                        headingColor={headingColor} mutedColor={mutedColor}
                        cardBg={cardBg} borderColor={borderColor}
                    />
                </Box>

                <CreateEventModal
                    isOpen={isCreateModalOpen} onClose={onCreateModalClose} selectedDate={selectedDate}
                    newEventTitle={newEventTitle}       setNewEventTitle={setNewEventTitle}
                    newEventDesc={newEventDesc}         setNewEventDesc={setNewEventDesc}
                    newEventStartTime={newEventStartTime} setNewEventStartTime={setNewEventStartTime}
                    newEventEndTime={newEventEndTime}   setNewEventEndTime={setNewEventEndTime}
                    newEventLocation={newEventLocation} setNewEventLocation={setNewEventLocation}
                    newEventPriority={newEventPriority} setNewEventPriority={setNewEventPriority}
                    newEventCategory={newEventCategory} setNewEventCategory={setNewEventCategory}
                    newEventAttendees={newEventAttendees} setNewEventAttendees={setNewEventAttendees}
                    newEventReminder={newEventReminder} setNewEventReminder={setNewEventReminder}
                    newEventRecurring={newEventRecurring} setNewEventRecurring={setNewEventRecurring}
                    newEventNotes={newEventNotes}       setNewEventNotes={setNewEventNotes}
                    onSubmit={handleCreateEventSubmit} isSubmitting={isSubmitting} onReset={resetEventForm}
                    technicians={technicians} borderColor={borderColor} inputBg={inputBg}
                />

                <DayDetailsModal
                    isOpen={isDayDetailsOpen} onClose={onDayDetailsClose}
                    day={selectedDayForDetails}
                    events={selectedDayForDetails ? (itemsByDate[format(selectedDayForDetails, 'yyyy-MM-dd')] || []) : []}
                    onCreateEvent={(day) => { onDayDetailsClose(); handleDayClick(day); }}
                    onEventClick={handleItemClick}
                />

                {selectedWorkOrder && (
                    <ViewWorkOrderModal
                        isOpen={isWorkOrderModalOpen}
                        onClose={() => { onWorkOrderModalClose(); setSelectedWorkOrder(null); }}
                        workOrderId={selectedWorkOrder.id}
                        onUpdate={loadData}
                    />
                )}

                <ConfirmDialog
                    isOpen={isDeleteConfirmOpen} onClose={onDeleteConfirmClose}
                    onConfirm={confirmDelete}
                    title="Delete Event"
                    message={`Are you sure you want to delete "${itemToDelete?.title}"? This action cannot be undone.`}
                    confirmText="Delete" confirmColorScheme="red"
                />
            </MotionBox>
        </ErrorBoundary>
    );
}

export default CalendarPage;